import { StyleSheet, css } from 'aphrodite';
import DOMPurify from 'dompurify';
import { type CSSProperties, type FC, useMemo, useRef, useState } from 'react';

import { useUserSession } from '../../../models/hooks/appStateHooks';
import { Button } from '../../../web/components-v2/Button';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';
import { CssSelectors } from '../../../web/styles/styles';
import { ApiClient } from '../../services/api';
import { RecordingPlaybackReporter } from '../../services/recordingPlayback';
import type { LeadHistoryEmailStatus, LeadHistoryEntry, LeadHistoryKind, LeadHistoryMeeting } from '../data';
import {
	IconBell,
	IconCalendarNote,
	IconClock,
	IconFileText,
	IconMail,
	IconPaperclip,
	IconPhone,
	IconTrophy,
	IconX,
} from './icons';

export type LeadHistoryCardProps = {
	entries: LeadHistoryEntry[] | undefined;
	isLoading: boolean;
	error: boolean;
	loadMoreError: boolean;
	hasNextPage: boolean;
	isFetchingNextPage: boolean;
	onLoadMore: () => void;
	onClose: () => void;
};

const ICON_BY_KIND: Record<LeadHistoryKind, FC<{ size?: number }>> = {
	'phone-calls': IconPhone,
	emails: IconMail,
	notes: IconFileText,
	meetings: IconCalendarNote,
	deals: IconTrophy,
	'follow-ups': IconBell,
};

type Tone = 'call' | 'email' | 'note' | 'deal' | 'meeting' | 'follow-up' | 'neutral';

const TONE_BY_KIND: Record<LeadHistoryKind, Tone> = {
	'phone-calls': 'call',
	emails: 'email',
	notes: 'note',
	meetings: 'meeting',
	deals: 'deal',
	'follow-ups': 'follow-up',
};

/** Sets `--tone` / `--tone-soft`, which a row's marker, details box and accents read. */
const toneVars = (tone: Tone) =>
	({ '--tone': `var(--history-${tone})`, '--tone-soft': `var(--history-${tone}-soft)` }) as CSSProperties;

const POSITIVE_OUTCOMES = new Set(['demo booked', 'demo rescheduled']);

const outcomeTone = (outcome: string): Tone => (POSITIVE_OUTCOMES.has(outcome.toLowerCase()) ? 'call' : 'neutral');

const UNKNOWN_DAY = 'unknown';

const parseDate = (timestamp: string | undefined) => {
	const parsed = timestamp ? new Date(timestamp) : undefined;
	return parsed && !Number.isNaN(parsed.getTime()) ? parsed : undefined;
};

const dayKey = (date: Date | undefined) =>
	date ? `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}` : UNKNOWN_DAY;

/** A lead's engagement history from every rep, for managers reviewing a lead beside its Game Plan. */
export const LeadHistoryCard: FC<LeadHistoryCardProps> = ({
	entries,
	isLoading,
	error,
	loadMoreError,
	hasNextPage,
	isFetchingNextPage,
	onLoadMore,
	onClose,
}) => {
	const userSession = useUserSession();
	const [playingId, setPlayingId] = useState<string | null>(null);
	const reporterRef = useRef<RecordingPlaybackReporter>(null);
	if (!reporterRef.current) {
		reporterRef.current = new RecordingPlaybackReporter(new ApiClient(userSession));
	}

	const days = useMemo(() => {
		const groups: { key: string; date: Date | undefined; entries: LeadHistoryEntry[] }[] = [];
		for (const entry of entries ?? []) {
			const date = parseDate(entry.timestamp);
			const key = dayKey(date);
			const last = groups[groups.length - 1];
			if (last?.key === key) {
				last.entries.push(entry);
			} else {
				groups.push({ key, date, entries: [entry] });
			}
		}
		return groups;
	}, [entries]);

	const togglePlay = (phoneCallId: string) => {
		reporterRef.current?.reset();
		setPlayingId(current => (current === phoneCallId ? null : phoneCallId));
	};

	const hasEntries = !!entries?.length;
	const lastContact = lastContactLabel(parseDate(entries?.[0]?.timestamp));
	const latestOutcome = entries?.find(entry => entry.outcome)?.outcome;
	return (
		<section className={css(styles.card)} aria-label='Lead history' data-testid='lead-history-card'>
			<header className={css(styles.header)}>
				<div>
					<div className={css(styles.title)}>Lead history</div>
					<div className={css(styles.subtitle)}>Every rep’s activity on this lead, newest first</div>
					{lastContact && (
						<div className={css(styles.lastContact)}>
							<span className={css(styles.lastContactLabel)}>Last contact</span>
							<span className={css(styles.lastContactValue)}>{lastContact}</span>
							{latestOutcome && (
								<span className={css(styles.outcomePill)} style={toneVars(outcomeTone(latestOutcome))}>
									{latestOutcome}
								</span>
							)}
						</div>
					)}
				</div>
				<Button variant='ghost' size='icon' onClick={onClose} aria-label='Close lead history'>
					<IconX size={14} />
				</Button>
			</header>
			<div className={css(styles.list)}>
				{isLoading && <div className={css(styles.empty)}>Loading…</div>}
				{error && !hasEntries && <div className={css(styles.empty)}>Couldn’t load lead history.</div>}
				{!isLoading && !error && !hasEntries && <div className={css(styles.empty)}>No activity on this lead yet.</div>}
				{days.map((day, dayIndex) => (
					<div key={`${day.key}-${dayIndex}`} className={css(styles.day)}>
						<div className={css(styles.dayHeader)}>
							{day.date
								? day.date.toLocaleDateString(undefined, {
										weekday: 'long',
										month: 'long',
										day: 'numeric',
										year: 'numeric',
									})
								: 'Unknown date'}
						</div>
						{day.entries.map((entry, index) => (
							<HistoryRow
								key={entry.id ?? `${day.key}-${index}`}
								entry={entry}
								isLastOfDay={index === day.entries.length - 1}
								playing={!!entry.recording && playingId === entry.recording.phoneCallId}
								onTogglePlay={() => entry.recording && togglePlay(entry.recording.phoneCallId)}
								onPlaybackStarted={() =>
									entry.recording &&
									reporterRef.current?.reportPlay({
										phoneCallId: entry.recording.phoneCallId,
										surface: 'focusedViewLeadHistory',
									})
								}
							/>
						))}
					</div>
				))}
				{loadMoreError && <div className={css(styles.loadMoreError)}>Couldn’t load older activity.</div>}
				{hasNextPage && (
					<Button
						variant='secondary'
						size='sm'
						styles={[styles.loadMore]}
						disabled={isFetchingNextPage}
						onClick={onLoadMore}
					>
						{isFetchingNextPage ? 'Loading…' : 'Load older activity'}
					</Button>
				)}
			</div>
		</section>
	);
};

type HistoryRowProps = {
	entry: LeadHistoryEntry;
	isLastOfDay: boolean;
	playing: boolean;
	onTogglePlay: () => void;
	onPlaybackStarted: () => void;
};

const HistoryRow: FC<HistoryRowProps> = ({ entry, isLastOfDay, playing, onTogglePlay, onPlaybackStarted }) => {
	const Icon = entry.kind ? ICON_BY_KIND[entry.kind] : IconClock;
	const when = parseDate(entry.timestamp);
	const timeLabel = when ? when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : undefined;
	const hasDetails = !!entry.bodyHtml || !!entry.attachments?.length || !!entry.recording;

	return (
		<article className={css(styles.row)} style={toneVars(entry.kind ? TONE_BY_KIND[entry.kind] : 'neutral')}>
			<div className={css(styles.rowTime)}>{timeLabel}</div>
			<div className={css(styles.railColumn)}>
				<div className={css(styles.rail, isLastOfDay && styles.railEnd)} />
				<div className={css(styles.marker)}>
					<Icon size={14} />
				</div>
			</div>
			<div className={css(styles.rowMain)}>
				<div className={css(styles.rowTitle)}>
					{entry.title || 'Activity'}
					{entry.outcome && (
						<span className={css(styles.rowOutcome)} style={toneVars(outcomeTone(entry.outcome))}>
							{` · ${entry.outcome}`}
						</span>
					)}
				</div>
				<div className={css(styles.rowMeta)}>
					{entry.rep && <span>by {entry.rep}</span>}
					{entry.email && <span className={css(styles.metaAccent)}>{emailStatusLabel(entry.email)}</span>}
					{entry.sync && (
						<span className={css(styles.metaAccent, styles.metaSync)} title={entry.sync.failure}>
							{entry.sync.label}
							{entry.sync.failure && <span className={css(styles.syncFailure)}> · sync failed</span>}
						</span>
					)}
				</div>
				{entry.summary && <div className={css(styles.rowSummary)}>{entry.summary}</div>}
				{hasDetails && (
					<div className={css(styles.details)}>
						{entry.bodyHtml && <EntryBody entry={entry} />}
						{!!entry.attachments?.length && (
							<ul className={css(styles.attachments)} aria-label='Attachments'>
								{entry.attachments.map(attachment => (
									<li key={attachment.id} className={css(styles.attachment)}>
										<IconPaperclip size={12} />
										{attachment.url ? (
											<a href={attachment.url} target='_blank' rel='noreferrer' className={css(styles.attachmentLink)}>
												{attachment.fileName}
											</a>
										) : (
											<span>{attachment.fileName}</span>
										)}
									</li>
								))}
							</ul>
						)}
						{entry.recording && (
							<div className={css(styles.recording)}>
								<Button variant='link' size='sm' styles={[styles.linkButton]} onClick={onTogglePlay}>
									{playing ? 'Close recording' : 'Play recording'}
								</Button>
								{playing && (
									<audio
										className={css(styles.player)}
										src={entry.recording.url}
										controls
										autoPlay
										onPlay={onPlaybackStarted}
									>
										<track kind='captions' />
									</audio>
								)}
							</div>
						)}
					</div>
				)}
				{entry.meeting && <MeetingDetails meeting={entry.meeting} />}
			</div>
		</article>
	);
};

/** Collapsed to the server's plain-text preview, like the legacy timeline; expands to the full rich body. */
const EntryBody: FC<{ entry: LeadHistoryEntry }> = ({ entry }) => {
	const [expanded, setExpanded] = useState(false);
	// Bodies are stored HTML, so sanitize before injecting: legacy-sourced or API-crafted content could carry
	// <script>, inline handlers or javascript: URLs.
	const html = useMemo(() => DOMPurify.sanitize(entry.bodyHtml ?? ''), [entry.bodyHtml]);
	const showPreview = !expanded && !!entry.preview && !!entry.hasMore;

	return (
		<div className={css(styles.body)}>
			{showPreview ? (
				<div className={css(styles.bodyPreview)}>{entry.preview}</div>
			) : (
				<div className={css(styles.bodyHtml)} dangerouslySetInnerHTML={{ __html: html }} />
			)}
			{!!entry.preview && entry.hasMore && (
				<Button
					variant='link'
					size='sm'
					styles={[styles.linkButton]}
					aria-expanded={expanded}
					onClick={() => setExpanded(value => !value)}
				>
					{expanded ? 'Show less' : 'Show more'}
				</Button>
			)}
		</div>
	);
};

const MeetingDetails: FC<{ meeting: LeadHistoryMeeting }> = ({ meeting }) => {
	const start = parseDate(meeting.startDate)?.toLocaleString(undefined, {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
	const heading = [meeting.title, start].filter(Boolean).join(' · ');
	const who = [meeting.contactName, meeting.phoneNumber, meeting.emailAddress].filter(Boolean).join(' · ');
	return (
		<div className={css(styles.meeting)} style={toneVars('deal')}>
			{heading && <div className={css(styles.meetingTitle)}>{heading}</div>}
			{who && <div className={css(styles.meetingRow)}>{who}</div>}
			{meeting.guests && <div className={css(styles.meetingRow)}>Guests: {meeting.guests}</div>}
		</div>
	);
};

function emailStatusLabel(status: LeadHistoryEmailStatus): string {
	const openedOn = parseDate(status.openDate)?.toLocaleDateString();
	switch (status.state) {
		case 'outside-levitate':
			return 'Sent outside of Levitate';
		case 'not-viewed':
			return 'Not viewed';
		case 'machine-opened':
			return `Automatically opened${openedOn ? ` on ${openedOn}` : ''}`;
		default:
			return `Viewed${openedOn ? ` on ${openedOn}` : ''}`;
	}
}

function lastContactLabel(latest: Date | undefined): string | undefined {
	if (!latest) return undefined;
	const minutes = Math.floor((Date.now() - latest.getTime()) / 60_000);
	if (minutes < 5) return 'just now';
	if (minutes < 60) return 'less than an hour ago';
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
	if (days < 28) {
		const weeks = Math.floor(days / 7);
		return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
	}
	if (days < 365) {
		const months = Math.max(1, Math.floor(days / 30));
		return `${months} month${months > 1 ? 's' : ''} ago`;
	}
	const years = Math.floor(days / 365);
	return `about ${years} year${years > 1 ? 's' : ''} ago`;
}

const TIME_GUTTER = 48;
const RAIL_COLUMN = 32;
const MARKER = 28;

const styles = StyleSheet.create({
	card: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minWidth: 0,
		minHeight: 0,
		background: 'var(--bg-elev)',
		borderRadius: THEME.common.borderRadius,
		border: '1px solid var(--border)',
		boxShadow: 'var(--shadow-sm)',
		overflow: 'hidden',
	},
	header: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: SPACE[8],
		padding: SPACE[16],
		background: 'var(--accent-soft)',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
	},
	title: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		color: 'var(--accent-ink)',
	},
	subtitle: {
		fontSize: THEME.typography.fontSizes.xs,
		color: 'var(--ink-muted)',
		marginTop: SPACE[4],
	},
	lastContact: {
		display: 'flex',
		alignItems: 'baseline',
		flexWrap: 'wrap',
		gap: SPACE[8],
		marginTop: SPACE[12],
	},
	lastContactLabel: {
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'var(--ink-muted)',
	},
	lastContactValue: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		color: 'var(--ink)',
	},
	outcomePill: {
		padding: `0 ${SPACE[8]}px`,
		borderRadius: 999,
		background: 'var(--tone-soft)',
		color: 'var(--tone)',
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	list: {
		flex: 1,
		overflowY: 'auto',
		minHeight: 0,
		padding: `${SPACE[16]}px ${SPACE[16]}px ${SPACE[24]}px`,
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[16],
	},
	empty: {
		padding: `${SPACE[24]}px ${SPACE[16]}px`,
		fontSize: THEME.typography.fontSizes.xs,
		color: 'var(--ink-muted)',
		textAlign: 'center',
	},
	day: {
		display: 'flex',
		flexDirection: 'column',
	},
	dayHeader: {
		alignSelf: 'flex-start',
		marginLeft: TIME_GUTTER + SPACE[8],
		marginBottom: SPACE[8],
		padding: `${SPACE[4]}px ${SPACE[12]}px`,
		borderRadius: 999,
		background: 'var(--history-day-bg)',
		color: 'var(--accent-ink)',
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	row: {
		display: 'grid',
		gridTemplateColumns: `${TIME_GUTTER}px ${RAIL_COLUMN}px minmax(0, 1fr)`,
		columnGap: `${SPACE[8]}px`,
	},
	rowTime: {
		paddingTop: SPACE[8],
		textAlign: 'right',
		fontSize: THEME.typography.fontSizes.xxs,
		color: 'var(--ink-muted)',
	},
	railColumn: {
		position: 'relative',
		display: 'flex',
		justifyContent: 'center',
	},
	rail: {
		position: 'absolute',
		top: 0,
		bottom: 0,
		width: 2,
		background: 'var(--history-rail)',
	},
	railEnd: {
		bottom: 'auto',
		height: SPACE[20],
	},
	marker: {
		position: 'relative',
		marginTop: SPACE[4],
		width: MARKER,
		height: MARKER,
		borderRadius: '50%',
		background: 'var(--tone)',
		color: 'var(--bg-elev)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		// Knocks the rail out around the marker.
		boxShadow: '0 0 0 4px var(--bg-elev)',
	},
	rowMain: {
		minWidth: 0,
		padding: `${SPACE[4]}px 0 ${SPACE[16]}px`,
	},
	rowTitle: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: 'var(--ink)',
	},
	rowOutcome: {
		color: 'var(--tone)',
		fontWeight: THEME.typography.fontWeights.bold,
	},
	rowSummary: {
		fontSize: THEME.typography.fontSizes.xs,
		color: 'var(--ink-muted)',
		marginTop: SPACE[4],
		whiteSpace: 'pre-wrap',
		overflowWrap: 'anywhere',
	},
	rowMeta: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: SPACE[8],
		marginTop: SPACE[4],
		fontSize: THEME.typography.fontSizes.xxs,
		color: 'var(--ink-muted)',
	},
	metaAccent: {
		color: 'var(--tone)',
		fontWeight: THEME.typography.fontWeights.bold,
	},
	metaSync: {
		color: 'var(--history-call)',
	},
	syncFailure: {
		color: 'var(--danger)',
	},
	details: {
		marginTop: SPACE[8],
		padding: `${SPACE[8]}px ${SPACE[12]}px`,
		borderRadius: THEME.common.borderRadius,
		background: 'var(--tone-soft)',
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[8],
	},
	body: {
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.5,
		color: 'var(--ink)',
	},
	bodyPreview: {
		whiteSpace: 'pre-wrap',
		overflowWrap: 'anywhere',
	},
	bodyHtml: {
		overflowWrap: 'anywhere',
		...CssSelectors.allDescendants({ margin: `0 0 ${SPACE[4]}px` }, 'p'),
		...CssSelectors.allDescendants({ maxWidth: '100%' }, 'img'),
	},
	linkButton: {
		padding: 0,
		height: 'auto',
		marginTop: SPACE[4],
	},
	meeting: {
		marginTop: SPACE[8],
		padding: `${SPACE[8]}px ${SPACE[12]}px`,
		borderRadius: THEME.common.borderRadius,
		background: 'var(--tone-soft)',
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.6,
		color: 'var(--ink)',
	},
	meetingTitle: {
		fontWeight: THEME.typography.fontWeights.bold,
		color: 'var(--tone)',
		marginBottom: SPACE[4],
	},
	meetingRow: {
		overflowWrap: 'anywhere',
	},
	attachments: {
		listStyle: 'none',
		margin: 0,
		padding: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
		fontSize: THEME.typography.fontSizes.xs,
	},
	attachment: {
		display: 'flex',
		alignItems: 'center',
		gap: SPACE[4],
		color: 'var(--tone)',
		minWidth: 0,
	},
	attachmentLink: {
		color: 'var(--accent-ink)',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	recording: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: SPACE[8],
	},
	player: {
		width: '100%',
		height: 32,
	},
	loadMore: {
		alignSelf: 'center',
		marginTop: SPACE[4],
	},
	loadMoreError: {
		fontSize: THEME.typography.fontSizes.xs,
		color: 'var(--ink-muted)',
		textAlign: 'center',
	},
});
